import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Upload, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { uploadFile } from "@/lib/api";
import NotificationSettings from "@/components/NotificationSettings";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DEFAULT_HOURS = Object.fromEntries(
  Array.from({ length: 7 }, (_, d) => [String(d), { enabled: d < 5, ranges: [["09:00", "12:00"], ["13:30", "18:00"]] }])
);

import ServicesSettings from "@/components/accounting/ServicesSettings";
import CompanySettings from "@/components/accounting/CompanySettings";

export default function Settings() {
  const { user } = useAuth();
  const [resetEmail, setResetEmail] = useState("");
  const [rates, setRates] = useState({ target_rate: 60, warn_threshold: 50, quote_followup_days: 5 });
  const [brand, setBrand] = useState({ app_name: "DGA", app_logo_url: "", app_favicon_url: "", brand_primary_color: "#0f172a", brand_secondary_color: "#a89b8c" });
  const [brandSaving, setBrandSaving] = useState(false);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [rdvPref, setRdvPref] = useState({ travel: 30, whatsapp: "" });
  const [booking, setBooking] = useState({
    booking_enabled: true, booking_slot_step: 30, booking_duration: 60,
    booking_brand_color: "#0f172a", booking_wa_message: "",
    booking_email_subject: "", booking_email_message: "", link: "",
  });
  const [bookingSaving, setBookingSaving] = useState(false);
  const logoInput = useRef(null);
  const faviconInput = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setRates({ target_rate: r.data.target_rate ?? 60, warn_threshold: r.data.warn_threshold ?? 50, quote_followup_days: r.data.quote_followup_days ?? 5 });
      setBrand({ app_name: r.data.app_name || "DGA", app_logo_url: r.data.app_logo_url || "",
        app_favicon_url: r.data.app_favicon_url || "",
        brand_primary_color: r.data.brand_primary_color || "#0f172a",
        brand_secondary_color: r.data.brand_secondary_color || "#a89b8c" });
      setHours(r.data.work_hours || DEFAULT_HOURS);
      setRdvPref({ travel: r.data.default_travel_minutes ?? 30, whatsapp: r.data.whatsapp_number || "" });
    }).catch(() => {});
    api.get("/booking/config").then((r) => {
      setBooking({
        booking_enabled: r.data.booking_enabled,
        booking_slot_step: r.data.booking_slot_step,
        booking_duration: r.data.booking_duration,
        booking_brand_color: r.data.booking_brand_color,
        booking_wa_message: r.data.booking_wa_message,
        booking_email_subject: r.data.booking_email_subject,
        booking_email_message: r.data.booking_email_message,
        link: r.data.link,
      });
    }).catch(() => {});
  }, []);

  const saveBooking = async () => {
    setBookingSaving(true);
    try {
      const { data } = await api.patch("/booking/config", {
        booking_enabled: booking.booking_enabled,
        booking_slot_step: Number(booking.booking_slot_step) || 30,
        booking_duration: Number(booking.booking_duration) || 60,
        booking_brand_color: booking.booking_brand_color,
        booking_wa_message: booking.booking_wa_message,
        booking_email_subject: booking.booking_email_subject,
        booking_email_message: booking.booking_email_message,
      });
      setBooking((b) => ({ ...b, link: data.link }));
      toast.success("Paramètres de réservation enregistrés");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBookingSaving(false); }
  };

  const saveRdvPref = async () => {
    try {
      await api.patch("/settings", {
        default_travel_minutes: Number(rdvPref.travel) || 0,
        whatsapp_number: rdvPref.whatsapp.trim(),
      });
      toast.success("Préférences RDV enregistrées");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const saveHours = async () => {
    try {
      await api.patch("/settings", { work_hours: hours });
      toast.success("Horaires de travail enregistrés");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandSaving(true);
    try {
      const data = await uploadFile(file);
      setBrand((b) => ({ ...b, app_logo_url: data.url }));
      toast.success("Logo téléversé — cliquez sur Enregistrer");
    } catch { toast.error("Erreur lors du téléversement"); }
    finally { setBrandSaving(false); e.target.value = ""; }
  };

  const uploadFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandSaving(true);
    try {
      const data = await uploadFile(file);
      setBrand((b) => ({ ...b, app_favicon_url: data.url }));
      toast.success("Favicon téléversé — cliquez sur Enregistrer");
    } catch { toast.error("Erreur lors du téléversement"); }
    finally { setBrandSaving(false); e.target.value = ""; }
  };

  const saveBrand = async () => {
    setBrandSaving(true);
    try {
      await api.patch("/settings", {
        app_name: brand.app_name.trim() || "DGA",
        app_logo_url: brand.app_logo_url,
        app_favicon_url: brand.app_favicon_url,
        brand_primary_color: brand.brand_primary_color,
        brand_secondary_color: brand.brand_secondary_color,
      });
      window.dispatchEvent(new Event("sf:brand"));
      toast.success("Image de marque enregistrée");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setBrandSaving(false); }
  };

  const saveRates = async () => {
    try {
      const { data } = await api.patch("/settings", rates);
      setRates(data);
      toast.success("Taux enregistrés");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const forgot = async () => {
    await api.post("/auth/forgot-password", { email: resetEmail || user?.email });
    toast.success("Si ce compte existe, un lien de réinitialisation a été généré (voir logs serveur en démo)");
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Paramètres</h1>
        <p className="text-neutral-500 text-sm mt-1">Votre compte et votre organisation.</p>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Profil</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Prénom</Label><Input data-testid="settings-name" className="mt-1.5" defaultValue={user?.name} readOnly /></div>
          <div><Label>Email</Label><Input data-testid="settings-email" className="mt-1.5" defaultValue={user?.email} readOnly /></div>
          <div><Label>Rôle</Label><Input className="mt-1.5 capitalize" defaultValue={user?.role} readOnly /></div>
          <div><Label>Organisation</Label><Input className="mt-1.5" defaultValue={user?.org?.name} readOnly /></div>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-3">
        <h3 className="font-heading font-bold text-lg">Sécurité</h3>
        <p className="text-sm text-neutral-500">Recevoir un lien de réinitialisation de mot de passe.</p>
        <div className="flex gap-2">
          <Input data-testid="settings-reset-email" placeholder={user?.email} value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
          <Button data-testid="settings-reset-btn" variant="outline" onClick={forgot}>Envoyer</Button>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Données financières / Rentabilité</h3>
        <p className="text-sm text-neutral-500">Seuil horaire minimum de rentabilité de l'agence. C'est le paramètre central utilisé par l'onglet Comptabilité → Rentabilité : statuts, marges, alertes et recommandations sont recalculés automatiquement dès sa modification.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Seuil horaire minimum de rentabilité (€/h)</Label>
            <Input data-testid="settings-target-rate" type="number" min="0" className="mt-1.5"
              value={rates.target_rate} onChange={(e) => setRates((r) => ({ ...r, target_rate: Number(e.target.value) }))} />
          </div>
          <div>
            <Label>Seuil « Peu rentable » (€/h) — page Suivi du temps</Label>
            <Input data-testid="settings-warn-threshold" type="number" min="0" className="mt-1.5"
              value={rates.warn_threshold} onChange={(e) => setRates((r) => ({ ...r, warn_threshold: Number(e.target.value) }))} />
          </div>
          <div>
            <Label>Relance devis — délai après envoi (jours)</Label>
            <Input data-testid="settings-quote-followup" type="number" min="1" className="mt-1.5"
              value={rates.quote_followup_days} onChange={(e) => setRates((r) => ({ ...r, quote_followup_days: Number(e.target.value) }))} />
            <p className="text-[10px] text-neutral-400 mt-1">Une tâche « Relancer le devis » est créée automatiquement dans la To-do à J+ ce délai.</p>
          </div>
        </div>
        <Button data-testid="settings-rates-save" onClick={saveRates} className="bg-neutral-900 hover:bg-neutral-800 text-white">Enregistrer</Button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="work-hours-settings">
        <h3 className="font-heading font-bold text-lg">Horaires de travail</h3>
        <p className="text-sm text-neutral-500">Utilisés par l'agenda pour la planification automatique et les alertes de capacité.</p>
        <div className="space-y-2.5">
          {DAYS.map((name, d) => {
            const conf = hours[String(d)] || { enabled: false, ranges: [] };
            const setDay = (patch) => setHours((h) => ({ ...h, [String(d)]: { ...conf, ...patch } }));
            const setRange = (i, j, v) => setHours((h) => ({
              ...h,
              [String(d)]: { ...conf, ranges: conf.ranges.map((r, k) => (k === i ? (j === 0 ? [v, r[1]] : [r[0], v]) : r)) },
            }));
            return (
              <div key={d} className="flex flex-wrap items-center gap-3" data-testid={`workday-${d}`}>
                <label className="flex items-center gap-2 w-28 cursor-pointer">
                  <input type="checkbox" className="accent-neutral-900" data-testid={`workday-enabled-${d}`}
                    checked={!!conf.enabled} onChange={(e) => setDay({ enabled: e.target.checked })} />
                  <span className="text-sm">{name}</span>
                </label>
                {conf.enabled ? (conf.ranges || []).map((r, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm">
                    <input type="time" value={r[0]} onChange={(e) => setRange(i, 0, e.target.value)} data-testid={`workday-start-${d}-${i}`}
                      className="border border-neutral-200 rounded-md px-2 py-1 text-sm bg-white" />
                    <span className="text-neutral-400">→</span>
                    <input type="time" value={r[1]} onChange={(e) => setRange(i, 1, e.target.value)} data-testid={`workday-end-${d}-${i}`}
                      className="border border-neutral-200 rounded-md px-2 py-1 text-sm bg-white" />
                  </span>
                )) : <span className="text-xs text-neutral-400">Repos</span>}
              </div>
            );
          })}
        </div>
        <Button data-testid="work-hours-save" onClick={saveHours} className="bg-neutral-900 hover:bg-neutral-800 text-white">Enregistrer</Button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="rdv-settings">
        <h3 className="font-heading font-bold text-lg">Rendez-vous & WhatsApp</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Temps de trajet moyen (minutes, aller simple)</Label>
            <Input type="number" min="0" className="mt-1.5" data-testid="settings-travel"
              value={rdvPref.travel} onChange={(e) => setRdvPref((p) => ({ ...p, travel: e.target.value }))} />
            <p className="text-xs text-neutral-400 mt-1">Appliqué automatiquement aux RDV en extérieur (modifiable dans chaque fiche RDV ; Genève = 60 min).</p>
          </div>
          <div>
            <Label>Numéro WhatsApp du CM</Label>
            <Input className="mt-1.5" data-testid="settings-whatsapp" value={rdvPref.whatsapp}
              onChange={(e) => setRdvPref((p) => ({ ...p, whatsapp: e.target.value }))} placeholder="+41 79 …" />
            <p className="text-xs text-neutral-400 mt-1">Les réponses WhatsApp des contacts y sont transférées (+ notification dans l'app).</p>
          </div>
        </div>
        <Button data-testid="rdv-settings-save" onClick={saveRdvPref} className="bg-neutral-900 hover:bg-neutral-800 text-white">Enregistrer</Button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="booking-settings">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Réservation en ligne</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-neutral-900" data-testid="booking-enabled-toggle"
              checked={!!booking.booking_enabled}
              onChange={(e) => setBooking((b) => ({ ...b, booking_enabled: e.target.checked }))} />
            <span className="text-sm">Activée</span>
          </label>
        </div>
        <p className="text-sm text-neutral-500">
          Vos clients réservent un RDV téléphonique via votre lien public. Les créneaux proposés suivent vos
          <strong> horaires de travail</strong> (section ci-dessus) moins les blocs déjà présents dans votre agenda.
          La page reprend le nom et le logo de la section <strong>Personnalisation</strong>.
        </p>
        {booking.link && (
          <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
            <p className="flex-1 text-xs text-neutral-600 truncate font-mono" data-testid="booking-settings-link">{booking.link}</p>
            <Button variant="outline" size="sm" data-testid="booking-settings-copy" className="gap-1.5"
              onClick={async () => { try { await navigator.clipboard.writeText(booking.link); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); } }}>
              <Copy className="w-3.5 h-3.5" /> Copier
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Intervalle des créneaux</Label>
            <select data-testid="booking-slot-step" className="mt-1.5 w-full border border-neutral-200 rounded-md px-2 py-2 text-sm bg-white"
              value={booking.booking_slot_step}
              onChange={(e) => setBooking((b) => ({ ...b, booking_slot_step: Number(e.target.value) }))}>
              <option value={15}>Toutes les 15 min</option>
              <option value={30}>Toutes les 30 min</option>
              <option value={60}>Toutes les heures</option>
            </select>
          </div>
          <div>
            <Label>Durée du RDV (minutes)</Label>
            <Input type="number" min="15" max="480" step="15" className="mt-1.5" data-testid="booking-duration"
              value={booking.booking_duration}
              onChange={(e) => setBooking((b) => ({ ...b, booking_duration: e.target.value }))} />
          </div>
          <div>
            <Label>Couleur de marque (page publique)</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" data-testid="booking-brand-color" className="w-9 h-9 rounded-md border border-neutral-200 cursor-pointer p-0.5 bg-white"
                value={booking.booking_brand_color}
                onChange={(e) => setBooking((b) => ({ ...b, booking_brand_color: e.target.value }))} />
              <Input className="flex-1 font-mono text-xs" data-testid="booking-brand-color-hex"
                value={booking.booking_brand_color}
                onChange={(e) => setBooking((b) => ({ ...b, booking_brand_color: e.target.value }))} />
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Message WhatsApp pré-rempli (le lien remplace {"{lien}"})</Label>
            <textarea rows={3} data-testid="booking-wa-template"
              className="mt-1.5 w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
              value={booking.booking_wa_message}
              onChange={(e) => setBooking((b) => ({ ...b, booking_wa_message: e.target.value }))} />
          </div>
          <div>
            <Label>Objet de l'email pré-rempli</Label>
            <Input className="mt-1.5" data-testid="booking-email-subject-template"
              value={booking.booking_email_subject}
              onChange={(e) => setBooking((b) => ({ ...b, booking_email_subject: e.target.value }))} />
          </div>
          <div>
            <Label>Message email pré-rempli (le lien remplace {"{lien}"})</Label>
            <textarea rows={4} data-testid="booking-email-template"
              className="mt-1.5 w-full border border-neutral-200 rounded-md px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
              value={booking.booking_email_message}
              onChange={(e) => setBooking((b) => ({ ...b, booking_email_message: e.target.value }))} />
          </div>
        </div>
        <Button data-testid="booking-settings-save" onClick={saveBooking} disabled={bookingSaving}
          className="bg-neutral-900 hover:bg-neutral-800 text-white">
          {bookingSaving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
      <NotificationSettings />
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="brand-settings">
        <h3 className="font-heading font-bold text-lg">Image de marque</h3>
        <p className="text-sm text-neutral-500">Nom, logo, favicon et couleurs affichés dans l'interface, les emails et la page publique de réservation.</p>
        <div className="flex items-center gap-4">
          {brand.app_logo_url ? (
            <img src={brand.app_logo_url} alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-neutral-200" data-testid="brand-logo-preview" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-neutral-900 flex items-center justify-center text-white text-sm font-bold">
              {(brand.app_name || "S")[0]}
            </div>
          )}
          <div className="flex-1">
            <Label>Nom de l'application</Label>
            <Input data-testid="brand-name" className="mt-1.5" value={brand.app_name}
              onChange={(e) => setBrand((b) => ({ ...b, app_name: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Logo</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={logoInput} type="file" accept="image/*" className="hidden" data-testid="brand-logo-input" onChange={uploadLogo} />
              <Button variant="outline" size="sm" data-testid="brand-logo-btn" onClick={() => logoInput.current?.click()} disabled={brandSaving} className="gap-1.5">
                <Upload className="w-3.5 h-3.5" /> Téléverser un logo
              </Button>
              {brand.app_logo_url && <span className="text-[11px] text-green-600">Logo défini</span>}
            </div>
          </div>
          <div>
            <Label>Favicon (onglet navigateur)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <input ref={faviconInput} type="file" accept="image/png,image/jpeg,image/webp,image/x-icon" className="hidden" data-testid="brand-favicon-input" onChange={uploadFavicon} />
              <Button variant="outline" size="sm" data-testid="brand-favicon-btn" onClick={() => faviconInput.current?.click()} disabled={brandSaving} className="gap-1.5">
                <Upload className="w-3.5 h-3.5" /> Téléverser un favicon
              </Button>
              {brand.app_favicon_url && (
                <img src={brand.app_favicon_url} alt="Favicon" className="w-6 h-6 rounded border border-neutral-200" data-testid="brand-favicon-preview" />
              )}
            </div>
            <p className="text-[11px] text-neutral-400 mt-1">Carré PNG recommandé (192×192 min). Appliqué à l'onglet après enregistrement.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Couleur principale</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" data-testid="brand-primary-color" className="w-9 h-9 rounded-md border border-neutral-200 cursor-pointer p-0.5 bg-white"
                value={brand.brand_primary_color}
                onChange={(e) => setBrand((b) => ({ ...b, brand_primary_color: e.target.value }))} />
              <Input className="flex-1 font-mono text-xs" data-testid="brand-primary-hex"
                value={brand.brand_primary_color}
                onChange={(e) => setBrand((b) => ({ ...b, brand_primary_color: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Couleur secondaire</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" data-testid="brand-secondary-color" className="w-9 h-9 rounded-md border border-neutral-200 cursor-pointer p-0.5 bg-white"
                value={brand.brand_secondary_color}
                onChange={(e) => setBrand((b) => ({ ...b, brand_secondary_color: e.target.value }))} />
              <Input className="flex-1 font-mono text-xs" data-testid="brand-secondary-hex"
                value={brand.brand_secondary_color}
                onChange={(e) => setBrand((b) => ({ ...b, brand_secondary_color: e.target.value }))} />
            </div>
          </div>
        </div>
        <Button data-testid="brand-save" onClick={saveBrand} disabled={brandSaving}
          className="bg-neutral-900 hover:bg-neutral-800 text-white">
          {brandSaving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
      <CompanySettings />
      <ServicesSettings />
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6">
        <h3 className="font-heading font-bold text-lg mb-2">Intégrations sociales</h3>
        <p className="text-sm text-neutral-500">
          Instagram, Facebook, LinkedIn, TikTok et Pinterest seront connectables ici (OAuth) pour la programmation
          automatique des publications validées. L'architecture est prête : statuts « Programmé » et « Publié » déjà actifs.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {["Instagram", "Facebook", "LinkedIn", "TikTok", "Pinterest"].map((n) => (
            <span key={n} className="text-xs border border-neutral-200 bg-neutral-50 text-neutral-500 rounded-full px-3 py-1.5" data-testid={`integration-${n.toLowerCase()}`}>
              {n} — bientôt
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
